import React, { Fragment, MouseEvent } from 'react';
import { carryParams } from 'utils';

import { NewSpeechifyLogo, NewSpeechifyLogoText } from 'assets/images';
import { PrimaryButton } from 'components/common/button/PrimaryButton';
import { useNavigate } from 'hooks/useNavigate';
import { useTranslation } from 'hooks/useTypedTranslation';
import { useUserAvatarImage } from 'modules/auth/hooks/useUserAvatarImage';
import { useIsDesktop } from 'modules/listening/hooks/useIsDesktop';
import { cn } from 'utils/cn';

import Head from '../elements/Head';
import Meta from '../elements/Meta';

type ErrorPageProps = {
  title: string;
  text: string;
  image: string;
};

export const ErrorPage: React.FC<ErrorPageProps> = ({ title, text, image }) => {
  const { t } = useTranslation('common');

  const avatarImage = useUserAvatarImage();
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();

  const handleAvatarClick = (event: MouseEvent) => {
    event.preventDefault();
    navigate('/settings');
  };

  const handleLogoClick = (event: MouseEvent) => {
    event.preventDefault();
    navigate(carryParams('/'));
  };

  const handleCTAClick = (event: MouseEvent) => {
    event.preventDefault();
    navigate(carryParams('/'));
  };

  return (
    <Fragment>
      <Head title={title} />
      <Meta />
      <div className="h-full flex flex-col justify-center min-h-screen bg-bg-prim-w-b text-center">
        <div className="w-full px-4 extraSmall:px-10 large:px-[120px] justify-between items-center inline-flex relative">
          <div className="flex flex-row items-center">
            <div className="flex items-center gap-[5px] extraSmall:py-0.5 rounded-lg cursor-pointer" onClick={handleLogoClick}>
              <div className="text-speechify-logo cursor-pointer">
                <NewSpeechifyLogo aria-hidden="true" />
              </div>
              <div className="text-speechify-logo-txt">
                <NewSpeechifyLogoText aria-hidden="true" />
              </div>
            </div>
          </div>
          <div className="flex items-center justify-end w-[462px] gap-2 extraSmall:gap-3 py-1.5 extraSmall:py-4">
            <div className="extraSmall:block">
              <button className="w-9 min-w-9 h-9 rounded-full flex items-center justify-center active:opacity-50, hover:opacity-75" onClick={handleAvatarClick}>
                <img
                  className="w-full h-full rounded-full object-cover"
                  src={avatarImage.avatarImageSrc || ''}
                  alt="T"
                  onError={avatarImage.avatarImageOnError}
                />
              </button>
            </div>
          </div>
        </div>

        <div className="flex flex-col items-center justify-center grow px-6 max-w-[440px] mx-auto">
          <img src={image} alt={title} className={cn('mb-8', isDesktop ? 'h-45' : 'h-40')} />
          <h1 className={cn('text-icn-txt-prim', isDesktop ? 'text-subheading-1' : 'text-subheading-3')}>{title}</h1>
          <p className={cn('text-icn-txt-prim mt-3', isDesktop ? 'text-body-5' : 'text-body-6')}>{text}</p>

          <PrimaryButton className="mt-8 text-heading-5 px-6 py-3 w-[280px]" onClick={handleCTAClick}>
            {t('Return to My Library')}
          </PrimaryButton>
        </div>

        <div className="text-icn-txt-sec text-body-6 mb-4">
          {t('Need help?')}{' '}
          <a href="https://help.speechify.com" className="underline">
            {t('Contact support')}
          </a>
        </div>
      </div>
    </Fragment>
  );
};
